import { PurlySignedUrlInfo } from '@jeppesen-foreflight/jmkw-types';
import { Persona } from '../../types/persona.model';

/**
 * Maps plugin/layer IDs to their corresponding Purly service identifiers
 */
export const LAYER_TO_PURLY_SERVICE_MAP: Record<string, string> = {
  // Cultural layer - uses the last part of the service name after the dot
  cultural: 'cultural',

  // Navigation layers (persona-specific) - uses the last part after the dot
  navigation: 'navigation',

  // Obstacles layer - uses the last part after the dot
  obstacles: 'obstacle',

  // Terrain layers - uses the last part after the dot
  'terrain-light': 'light',
  'terrain-dark': 'dark'
};

/**
 * Maps personas to their corresponding navigation service identifiers
 */
export const PERSONA_TO_NAVIGATION_SERVICE_MAP: Record<Persona, string> = {
  InFlight: 'InFlight',
  Operations: 'Operations'
};

/**
 * Extracts the URL for a specific layer from the Purly response
 *
 * @param layerId The layer ID to look up
 * @param purlyUrls The array or categorized record of Purly signed URLs
 * @param persona Optional persona for navigation layers
 * @returns The URL for the specified layer, or empty string if not found
 */
export function getPurlyUrlForLayer(
  layerId: 'cultural' | 'navigation' | 'obstacles',
  tileServiceUrls: Record<string, PurlySignedUrlInfo>,
  persona?: Persona
): string {
  // Handle navigation layers with persona
  if (layerId === 'navigation' && persona) {
    const navigationUrls = getPurlyNavigationUrls(tileServiceUrls);
    return navigationUrls[persona];
  }

  // Handle other layers
  const serviceId = LAYER_TO_PURLY_SERVICE_MAP[layerId];
  if (!serviceId) {
    return '';
  }

  // For cultural and obstacles, extract the URL from the PurlySignedUrlInfo object
  const urlInfo = tileServiceUrls[serviceId];
  if (!urlInfo) {
    return '';
  }

  return urlInfo.url;
}

/**
 * Finds a URL by service ID in the Purly response
 *
 * @param serviceId The service ID to search for
 * @param purlyUrls The Purly URLs (can be array or categorized record)
 * @returns The URL if found, empty string otherwise
 */
function findUrlByServiceId(
  serviceId: string,
  purlyUrls: PurlySignedUrlInfo[] | Record<string, PurlySignedUrlInfo>
): string {
  // If it's an array of URLs (like from the tiles array in the response)
  if (Array.isArray(purlyUrls)) {
    const urlInfo = purlyUrls.find((url) => url.svc === serviceId);
    return urlInfo?.url || '';
  }

  // If it's a categorized record structure (Record<string, PurlySignedUrlInfo>)
  // Check if the serviceId directly exists as a key
  if (serviceId in purlyUrls) {
    return purlyUrls[serviceId]?.url || '';
  }

  // Also check by service name in the svc property for objects
  for (const urlInfo of Object.values(purlyUrls)) {
    if (
      urlInfo &&
      typeof urlInfo === 'object' &&
      'svc' in urlInfo &&
      urlInfo.svc === serviceId
    ) {
      return urlInfo.url || '';
    }
  }

  return '';
}

/**
 * Gets all navigation URLs from Purly response, organized by persona
 *
 * @param purlyUrls The Purly response URLs
 * @returns Record mapping persona to URL
 */
export function getPurlyNavigationUrls(
  purlyUrls: PurlySignedUrlInfo[] | Record<string, PurlySignedUrlInfo>
): Record<Persona, string> {
  const result: Record<Persona, string> = {
    InFlight: '',
    Operations: ''
  };

  // If it's an array, look for the full service IDs
  if (Array.isArray(purlyUrls)) {
    for (const [persona, _] of Object.entries(
      PERSONA_TO_NAVIGATION_SERVICE_MAP
    )) {
      const fullServiceId = `tiles.navigation.${persona}`;
      result[persona as Persona] = findUrlByServiceId(fullServiceId, purlyUrls);
    }
  } else {
    // If it's a record, use the mapping values (which might be short names or full service IDs)
    for (const [persona, serviceId] of Object.entries(
      PERSONA_TO_NAVIGATION_SERVICE_MAP
    )) {
      result[persona as Persona] = findUrlByServiceId(serviceId, purlyUrls);
    }
  }

  return result;
}

/**
 * Gets terrain URLs from Purly response
 *
 * @param purlyUrls The Purly response URLs
 * @returns Object with Light and Dark terrain URLs
 */
export function getPurlyTerrainUrls(
  purlyUrls: PurlySignedUrlInfo[] | Record<string, PurlySignedUrlInfo>
): { Light: string; Dark: string } {
  return {
    Light: findUrlByServiceId('light', purlyUrls),
    Dark: findUrlByServiceId('dark', purlyUrls)
  };
}
